package org.teddapp.project.ui.finance

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.launch
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.LocalUseFullScreenForm
import org.teddapp.project.ui.TeddBottomSheet
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.projects.ProjectsConfirmDelete
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.SettingsScreen
import org.teddapp.project.ui.settings.rememberSettingsDrawer

/**
 * Écran Finance unique. La logique vit dans [FinanceModel] ; la mise en page
 * s'adapte à la largeur : compacte avec bandeau de compte, onglets, bouton
 * flottant et feuilles ; étendue avec rail, barre latérale des comptes et
 * bandeau détaillé. Les dialogues CRUD sont les mêmes partout.
 */
@Composable
fun FinanceScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
    /** Onglet ouvert à l'arrivée, par sa clé (« simulation ») ; pour les captures. */
    initialTab: String? = null,
) {
    val model = rememberFinanceModel(repos)
    LaunchedEffect(initialTab) { FinanceTab.entries.firstOrNull { it.key == initialTab }?.let { model.tab = it } }
    val hideBalance = LocalAppSettings.current.hideBalance
    WithWindowClass(modifier.fillMaxSize()) { wc ->
        CompositionLocalProvider(
            LocalHideBalance provides hideBalance,
            LocalUseFullScreenForm provides (wc == WindowClass.Compact),
        ) {
            when (wc) {
                WindowClass.Compact -> FinanceCompactLayout(model, repos, onNavigate)
                else -> FinanceExpandedLayout(model, repos, onNavigate)
            }
        }
    }
}

/** Contenu de l'onglet courant, identique dans les deux mises en page. */
@Composable
private fun FinanceTabContent(model: FinanceModel, compact: Boolean, onSeeAll: (() -> Unit)?) {
    when (model.tab) {
        FinanceTab.Transactions -> TransactionsTab(
            txns = model.accTxns,
            categories = model.categories,
            accounts = model.accounts,
            currentAccountId = model.selectedId,
            filter = model.txFilter,
            onFilterChange = { model.txFilter = it },
            period = model.txPeriod,
            onPeriodChange = { model.txPeriod = it },
            categoryId = model.txCategoryId,
            onCategoryChange = { model.txCategoryId = it },
            search = model.search,
            onSearchChange = { model.search = it },
            compact = compact,
            onSeeAll = onSeeAll,
            onEditTx = { model.editingTx = it },
        )
        FinanceTab.Dashboard -> DashboardTab(
            txns = model.accTxns,
            categories = model.categories,
            currentAccountId = model.selectedId,
            currency = model.currency,
            compact = compact,
            onManageCategories = { model.categoryManagerOpen = true },
        )
        FinanceTab.Patrimoine -> PatrimoineTab(
            assets = model.assets,
            valuations = model.valuations,
            accounts = model.accounts,
            loans = model.loans,
            loanPayments = model.loanPayments,
            hideBalance = LocalAppSettings.current.hideBalance,
            compact = compact,
            onCreate = { model.creatingAsset = true },
            onEdit = { model.editingAsset = it },
            onValue = { model.valuingAsset = it },
            onDelete = { model.deleteAsset(it) },
            onCreateLoan = { model.creatingLoan = true },
            onEditLoan = { model.editingLoan = it },
            onRepayLoan = { model.repayingLoan = it },
            onLoanPayments = { model.viewingLoanPayments = it },
            onDeleteLoan = { model.confirmDeleteLoan = it },
        )
        FinanceTab.Lists -> ListsTab(
            lists = model.expenseLists,
            items = model.expenseListItems,
            categories = model.categories,
            expandedId = model.expandedListId,
            onExpand = { model.toggleExpanded(it) },
            currency = model.currency,
            onRequestMarkDone = { model.markingItem = it },
            onUntick = { model.untickItem(it) },
            onCreateList = { model.creatingList = true },
            onEditList = { model.editingList = it },
            onAddItem = { listId, name, planned, catId, count -> model.addItem(listId, name, planned, catId, count) },
            onDeleteItem = { model.deleteItem(it) },
            onEditItem = { item, name, planned, catId -> model.editItem(item, name, planned, catId) },
            onCloneList = { model.cloneList(it) },
            onSendToPomodoro = { model.sendItemToPomodoro(it) },
            entries = model.expenseEntries,
            onOpenWallet = { model.walletItem = it },
        )
        FinanceTab.Simulation -> SimulationTab(
            simulations = model.simulations,
            lines = model.simulationLines,
            selected = model.selectedSimulation,
            onSelect = { model.selectedSimulationId = it },
            currency = model.currency,
            compact = compact,
            onCreate = { model.creatingSimulation = true },
            onRename = { model.renamingSimulation = it },
            onDuplicate = { model.duplicateSimulation(it) },
            onDelete = { model.confirmDeleteSimulation = it },
            onAddLine = { id, label, expr -> model.addSimulationLine(id, label, expr) },
            onEditLine = { line, label, expr -> model.editSimulationLine(line, label, expr) },
            onDeleteLine = { model.deleteSimulationLine(it) },
            onMoveLine = { line, delta -> model.moveSimulationLine(line, delta) },
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun FinanceExpandedLayout(model: FinanceModel, repos: TeddRepositories, onNavigate: (Section) -> Unit) {
    val appSettings = LocalAppSettings.current
    val drawer = rememberSettingsDrawer()
    SettingsDrawerHost(drawer, onNavigate = onNavigate) {
        Column(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            Row(modifier = Modifier.fillMaxSize()) {
                TeddNavRail(active = Section.Finance, onNavigate = onNavigate, onSettingsClick = drawer::open)
                FinanceSidebar(
                    accounts = model.accounts,
                    selectedId = model.selectedId,
                    onSelect = { model.selectedAccountId = it },
                    defaultAccountId = model.defaultAccountId,
                    onToggleDefault = { model.toggleDefaultAccount(it) },
                    monthTxns = model.accTxns.filter { it.date in MONTH_START..MONTH_END && it.type == "expense" },
                    categories = model.categories,
                    hideBalance = appSettings.hideBalance,
                    onToggleHide = { appSettings.applyHideBalance(!appSettings.hideBalance) },
                    onCreateAccount = { model.creatingAccount = true },
                )
                Column(modifier = Modifier.weight(1f).fillMaxHeight().background(TeddTokens.Bg)) {
                    FinanceHero(
                        account = model.account,
                        txns = model.accTxns,
                        onEditAccount = { model.account?.let { model.editingAccount = it } },
                    )
                    FinanceTabBar(
                        tab = model.tab,
                        onTabChange = { model.tab = it },
                        txnCount = model.accTxns.size,
                        listsCount = model.expenseLists.size,
                        assetsCount = model.assets.count { it.is_deleted == 0L } + model.loans.count { it.is_deleted == 0L && it.status == "open" },
                        simulationsCount = model.simulations.size,
                        onAddClick = { model.showAdd = true },
                    )
                    Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                        FinanceTabContent(model = model, compact = false, onSeeAll = null)
                    }
                }
            }
        }
    }
    FinanceDialogs(model, repos)
    TeddSnackbarHost(state = model.snackbar)
}

// ──────────────────────────────────────────────────────────────────────
// Compact
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun FinanceCompactLayout(model: FinanceModel, repos: TeddRepositories, onNavigate: (Section) -> Unit) {
    val appSettings = LocalAppSettings.current
    var showQuickAdd by remember { mutableStateOf(false) }
    var showSwitcher by remember { mutableStateOf(false) }
    var showAllTx by remember { mutableStateOf(false) }
    var showSettings by remember { mutableStateOf(false) }
    val account = model.account

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            FinanceCompactHeader(
                onToggleHide = { appSettings.applyHideBalance(!appSettings.hideBalance) },
                hideBalance = appSettings.hideBalance,
                onOpenSettings = { showSettings = true },
            )
            if (account != null) {
                // Le bloc du solde et les onglets s'effacent quand on fait défiler : le contenu
                // prend alors toute la hauteur. Hors des transactions, le solde tient déjà sur une ligne.
                org.teddapp.project.ui.TeddCollapsingLayout(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    header = {
                        Column {
                            if (model.tab == FinanceTab.Transactions) {
                                FinanceCompactHero(
                                    account = account,
                                    txns = model.accTxns,
                                    accountCount = model.activeAccounts.size,
                                    onOpenSwitcher = { showSwitcher = true },
                                    onEdit = { model.editingAccount = account },
                                )
                            } else {
                                FinanceCompactHeroSlim(account = account, accountCount = model.activeAccounts.size, onOpenSwitcher = { showSwitcher = true })
                            }
                            FinanceCompactTabBar(
                                tab = model.tab,
                                onTabChange = { model.tab = it },
                                txnCount = model.accTxns.size,
                                listsCount = model.expenseLists.size,
                                assetsCount = model.assets.count { it.is_deleted == 0L } + model.loans.count { it.is_deleted == 0L && it.status == "open" },
                                simulationsCount = model.simulations.size,
                            )
                        }
                    },
                ) {
                    Box(modifier = Modifier.fillMaxSize()) {
                        FinanceTabContent(model = model, compact = true, onSeeAll = { showAllTx = true })
                    }
                }
            } else {
                FinanceNoAccount(modifier = Modifier.weight(1f), onCreate = { model.creatingAccount = true })
            }
            TeddBottomNav(active = Section.Finance, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }

        if (account != null) {
            Box(modifier = Modifier.align(Alignment.BottomEnd).padding(end = 20.dp, bottom = 88.dp)) {
                FinanceCompactFab(onClick = { showQuickAdd = true })
            }
            TeddBottomSheet(visible = showSwitcher, onDismiss = { showSwitcher = false }) {
                FinanceAccountSwitcherSheet(
                    accounts = model.activeAccounts,
                    selectedId = model.selectedId,
                    onSelect = { model.selectedAccountId = it },
                    defaultAccountId = model.defaultAccountId,
                    onToggleDefault = { model.toggleDefaultAccount(it) },
                    onCreate = { model.creatingAccount = true },
                    onDismiss = { showSwitcher = false },
                )
            }
            TeddBottomSheet(visible = showQuickAdd, onDismiss = { showQuickAdd = false }) {
                FinanceQuickAddSheet(
                    account = account,
                    categories = model.categories,
                    onDismiss = { showQuickAdd = false },
                    onAdvanced = { model.showAdd = true },
                    onSave = { type, amount, description, catId ->
                        model.createTx(account.id, TODAY_ISO, type, amount, description.ifBlank { null }, catId, null)
                    },
                )
            }
            if (showAllTx) {
                AllTransactionsScreen(
                    account = account,
                    txns = model.accTxns,
                    categories = model.categories,
                    accounts = model.accounts,
                    currentAccountId = model.selectedId,
                    onClose = { showAllTx = false },
                    onEditTx = { model.editingTx = it },
                )
            }
        }
        if (showSettings) {
            Dialog(
                onDismissRequest = { showSettings = false },
                properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = false),
            ) { SettingsScreen(onClose = { showSettings = false }, onOpenBackup = { showSettings = false; onNavigate(Section.Backup) }, modifier = Modifier.fillMaxSize()) }
        }
        TeddSnackbarHost(state = model.snackbar)
        FinanceDialogs(model, repos)
    }
}

@Composable
private fun FinanceNoAccount(modifier: Modifier, onCreate: () -> Unit) {
    Column(
        modifier = modifier.fillMaxWidth().padding(28.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Box(
            modifier = Modifier.size(56.dp).clip(RoundedCornerShape(TeddTokens.RMd)).background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RMd)),
            contentAlignment = Alignment.Center,
        ) { TeddIcon(name = "wallet", size = 28.dp, color = TeddTokens.LimeStrong) }
        Spacer(modifier = Modifier.height(14.dp))
        Text("Aucun compte", color = TeddTokens.Ink, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
        Spacer(modifier = Modifier.height(4.dp))
        Text("Crée ton premier compte pour démarrer.", color = TeddTokens.InkMuted, fontSize = 12.sp)
        Spacer(modifier = Modifier.height(16.dp))
        TeddButton(
            text = "Nouveau compte", onClick = onCreate, kind = TeddButtonKind.Primary, size = TeddButtonSize.Md,
            leadingIcon = { TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// Dialogues CRUD, écrits une fois pour toutes les largeurs
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun FinanceDialogs(model: FinanceModel, repos: TeddRepositories) {
    if (model.creatingSimulation) {
        SimulationNameDialog(existing = null, onClose = { model.creatingSimulation = false }, onSave = { name, note -> model.createSimulation(name, note) })
    }
    model.renamingSimulation?.let { sim ->
        SimulationNameDialog(existing = sim, onClose = { model.renamingSimulation = null }, onSave = { name, note -> model.renameSimulation(sim, name, note) })
    }
    model.confirmDeleteSimulation?.let { sim ->
        val n = model.simulationLines.count { it.simulation_id == sim.id }
        org.teddapp.project.ui.TeddConfirmDialog(
            title = "Supprimer ce brouillon ?",
            message = "« ${sim.name} » et ${if (n <= 1) "sa ligne" else "ses $n lignes"} seront supprimés. Il n'y a pas de corbeille.",
            confirmLabel = "Supprimer",
            onConfirm = { model.deleteSimulation(sim) },
            onDismiss = { model.confirmDeleteSimulation = null },
        )
    }
    if (model.creatingAsset || model.editingAsset != null) {
        AssetEditor(
            existing = model.editingAsset,
            defaultCurrency = model.currency,
            onClose = { model.creatingAsset = false; model.editingAsset = null },
            onSave = { name, kind, currency, value, price, acquired, note ->
                model.saveAsset(model.editingAsset, name, kind, currency, value, price, acquired, note)
            },
        )
    }
    if (model.creatingLoan || model.editingLoan != null) {
        LoanEditor(
            existing = model.editingLoan,
            accounts = model.accounts,
            preferredAccountId = model.selectedId,
            defaultCurrency = model.currency,
            onClose = { model.creatingLoan = false; model.editingLoan = null },
            onCreate = { direction, counterparty, principal, interest, date, dueDate, note, accountId ->
                model.createLoan(direction, counterparty, principal, interest, date, dueDate, note, accountId)
            },
            onUpdate = { loan, counterparty, interest, dueDate, note -> model.updateLoan(loan, counterparty, interest, dueDate, note) },
        )
    }
    model.repayingLoan?.let { loan ->
        LoanRepayDialog(
            loan = loan,
            payments = model.loanPayments,
            accounts = model.accounts,
            preferredAccountId = model.selectedId,
            onClose = { model.repayingLoan = null },
            onSave = { amount, date, accountId, note -> model.repayLoan(loan, amount, date, accountId, note) },
        )
    }
    model.viewingLoanPayments?.let { open ->
        // Le prêt à jour : son statut bouge quand un remboursement se retire.
        val loan = model.loans.firstOrNull { it.id == open.id } ?: open
        LoanPaymentsDialog(
            loan = loan,
            payments = model.loanPayments,
            accounts = model.accounts,
            onClose = { model.viewingLoanPayments = null },
            onDeletePayment = { model.deleteLoanPayment(it) },
        )
    }
    model.confirmDeleteLoan?.let { loan ->
        val n = model.loanPayments.count { it.loan_id == loan.id && it.is_deleted == 0L }
        org.teddapp.project.ui.TeddConfirmDialog(
            title = "Supprimer ce prêt ?",
            message = buildString {
                append("Le prêt avec ${loan.counterparty}")
                if (n > 0) append(", ${if (n == 1) "son remboursement" else "ses $n remboursements"}")
                append(" et leurs mouvements seront retirés ; les soldes reviennent à ce qu'ils étaient. Il n'y a pas de corbeille.")
            },
            confirmLabel = "Supprimer",
            onConfirm = { model.deleteLoan(loan) },
            onDismiss = { model.confirmDeleteLoan = null },
        )
    }
    model.valuingAsset?.let { asset ->
        AssetValuationDialog(
            asset = asset,
            history = model.valuations.filter { it.asset_id == asset.id && it.is_deleted == 0L },
            onClose = { model.valuingAsset = null },
            onSave = { value, date, note -> model.recordValuation(asset, value, date, note) },
        )
    }
    if (model.categoryManagerOpen) {
        FinanceCategoryManager(
            categories = model.categories,
            repos = repos,
            onClose = { model.categoryManagerOpen = false },
        )
    }
    model.pendingExpense?.let { pending ->
        CartesianFilterScreen(
            pending = pending,
            categories = model.categories,
            onNeed = { model.confirmPendingAsNeed() },
            onMastered = { model.masterPendingDesire() },
            onDismiss = { model.dismissPendingExpense() },
        )
    }
    val scope = rememberCoroutineScope()

    model.walletItem?.let { open ->
        // La ligne à jour : son réel bouge à chaque sous-dépense.
        val item = model.expenseListItems.firstOrNull { it.id == open.id } ?: open
        ExpenseWalletPanel(
            item = item,
            entries = model.expenseEntries.filter { it.item_id == item.id },
            currency = model.currency,
            accounts = model.accounts,
            preferredAccountId = model.selectedId,
            onAdd = { label, amount, date -> model.addEntry(item, label, amount, date, null) },
            onAddPaid = { label, amount, date, accountId -> model.addEntryPaid(item, label, amount, date, accountId) },
            onUpdate = { e, label, amount, date -> model.updateEntry(e, label, amount, date, e.note) },
            onDelete = { model.deleteEntry(it) },
            onClose = { model.walletItem = null },
        )
    }

    model.markingItem?.let { item ->
        val listName = model.expenseLists.firstOrNull { it.id == item.list_id }?.name ?: "—"
        MarkItemDonePanel(
            item = item,
            listName = listName,
            categories = model.categories,
            accounts = model.accounts,
            defaultAccountId = model.selectedId,
            alreadyPaid = model.paidForItem(item.id),
            onClose = { model.markingItem = null },
            onConfirm = { actual, accId, date, desc, _ -> model.markItemDone(item, actual, accId, date, desc) },
        )
    }

    if (model.editingList != null || model.creatingList) {
        val existing = model.editingList
        ListEditPanel(
            existing = existing,
            onClose = { model.editingList = null; model.creatingList = false },
            onSave = { draft ->
                scope.launch {
                    val listId = if (existing == null) {
                        repos.expenseLists.createList(
                            name = draft.name, description = draft.description, status = draft.status,
                            date = draft.date, orderIndex = model.expenseLists.size.toLong(),
                        )
                    } else {
                        repos.expenseLists.updateList(
                            id = existing.id, name = draft.name, description = draft.description,
                            status = draft.status, date = draft.date, orderIndex = existing.order_index,
                        )
                        existing.id
                    }
                    applyListRecurrence(repos, listId, draft.recurrence, draft.resetMode, existing)
                }
                model.editingList = null
                model.creatingList = false
            },
            onDelete = existing?.let { l -> { model.deleteList(l) } },
        )
    }

    if (model.editingAccount != null || model.creatingAccount) {
        val existing = model.editingAccount
        AccountEditPanel(
            existing = existing,
            onClose = { model.editingAccount = null; model.creatingAccount = false },
            onSave = { draft ->
                scope.launch {
                    if (existing == null) {
                        val newId = repos.financeAccounts.create(
                            name = draft.name, type = draft.type, currency = draft.currency, balance = draft.balance,
                            icon = draft.icon, color = draft.color, bank = draft.bank, last4 = draft.last4,
                            lowThreshold = draft.lowThreshold, highThreshold = draft.highThreshold,
                            orderIndex = model.accounts.size.toLong(),
                        )
                        model.selectedAccountId = newId
                    } else {
                        repos.financeAccounts.update(
                            id = existing.id, name = draft.name, type = draft.type, currency = draft.currency,
                            icon = draft.icon, color = draft.color, bank = draft.bank, last4 = draft.last4,
                            lowThreshold = draft.lowThreshold, highThreshold = draft.highThreshold,
                            orderIndex = existing.order_index,
                        )
                        if (draft.balance != existing.balance) repos.financeAccounts.setBalance(existing.id, draft.balance)
                    }
                }
                model.editingAccount = null
                model.creatingAccount = false
            },
            onDelete = existing?.let { a -> { model.deleteAccount(a) } },
        )
    }

    val account = model.account
    if (model.showAdd && account != null) {
        AddTransactionPanel(
            account = account,
            accounts = model.accounts,
            categories = model.categories,
            onCreateCategory = { nom, type -> model.createCategory(nom, type) },
            onClose = { model.showAdd = false },
            onSave = { date, type, amount, description, catId, toAccountId, _ ->
                model.createTx(account.id, date, type, amount, description, catId, toAccountId)
                model.showAdd = false
            },
        )
    }

    model.editingTx?.let { tx ->
        val txAccount = model.accounts.firstOrNull { it.id == tx.account_id }
        if (txAccount != null) {
            AddTransactionPanel(
                account = txAccount,
                accounts = model.accounts,
                categories = model.categories,
                existing = tx,
                onCreateCategory = { nom, type -> model.createCategory(nom, type) },
                onClose = { model.editingTx = null },
                onSave = { date, type, amount, description, catId, toAccountId, _ ->
                    model.updateTx(tx, date, type, amount, description, catId, toAccountId)
                    model.editingTx = null
                },
                onDelete = { model.confirmDeleteTx = tx },
            )
        }
    }

    model.confirmDeleteTx?.let { tx ->
        ProjectsConfirmDelete(
            title = "Supprimer la transaction ?",
            itemName = tx.description?.ifBlank { null } ?: "Transaction du ${tx.date}",
            description = "Le solde du compte sera réajusté en conséquence.",
            onCancel = { model.confirmDeleteTx = null },
            onConfirm = { model.deleteTx(tx) },
        )
    }
}
